import { useAuth } from "../auth/AuthProvider";
import { useAsync } from "../hooks/useAsync";
import { RatioCell, StateBlock, money, count } from "../components/StateBlock";
import type { CacLtvResponse } from "../api/types";

export function MarketingMetrics({ brandId }: { brandId: string }) {
  const { api } = useAuth();
  const enabled = brandId.trim().length > 0;
  const { data, loading, error, reload } = useAsync<CacLtvResponse>(
    (signal) => api.getCacLtv(brandId.trim(), signal),
    [brandId],
    enabled,
  );

  if (!enabled) {
    return (
      <div className="state state--empty" role="status">
        Enter a brand ID above to load CAC / LTV metrics.
      </div>
    );
  }

  const channels = data?.channels ?? [];
  const currency = data?.currency ?? "USD";

  return (
    <section aria-labelledby="mm-heading">
      <h2 id="mm-heading">Marketing Metrics — CAC &amp; LTV</h2>
      <p className="section-note">
        Per-channel customer acquisition cost, gross lifetime value
        (trailing-90-day realized + projected-90-day run-rate) and the LTV/CAC
        ratio.
      </p>
      <StateBlock
        loading={loading}
        error={error}
        isEmpty={channels.length === 0}
        emptyMessage="No channel metrics available for this brand yet."
        onRetry={reload}
      >
        <div className="table-wrap">
          <table>
            <caption className="sr-only">CAC and LTV per channel</caption>
            <thead>
              <tr>
                <th scope="col">Channel</th>
                <th scope="col" className="num">
                  CAC
                </th>
                <th scope="col" className="num">
                  LTV (trailing)
                </th>
                <th scope="col" className="num">
                  LTV (projected)
                </th>
                <th scope="col" className="num">
                  LTV (gross)
                </th>
                <th scope="col" className="num">
                  LTV / CAC
                </th>
                <th scope="col" className="num">
                  Customers
                </th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.channel}>
                  <th scope="row" className="channel">
                    {c.channel}
                  </th>
                  <td className="num">
                    <RatioCell ratio={c.cac} />
                  </td>
                  <td className="num">{money(c.ltvTrailing, currency)}</td>
                  <td className="num">{money(c.ltvProjected, currency)}</td>
                  <td className="num">{money(c.ltvGross, currency)}</td>
                  <td className="num">
                    <RatioCell ratio={c.ltvCacRatio} suffix="×" />
                  </td>
                  <td className="num">{count(c.customerCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StateBlock>
    </section>
  );
}
